import { inject } from 'inversify';

import { service } from '@/decorators/service';
import { SignalrListener, SignalrMessage } from '@/decorators/signalr';
import { ResponseProfile } from '@/entities/ResponseProfile';
import { PriorityManager } from '@/managers/PriorityManager';
import { Logger } from '@/services/Logger';
import { Signalr } from '@/services/signalr/Signalr';
import { ResponseProfileStore } from '@/stores/ResponseProfileStore';
import type { IResponseProfile } from '~/api/IResponseProfile';
import { IResponseTimeStamp } from '~/api/IResponseTimeStamp';
import type { IStatusTimeInfo } from '~/api/IStatusTimeInfo';
import type { IResponseProfileRespMsg } from '~/api/messages/IResponseProfileRespMsg';

@SignalrListener
@service(ResponseProfileManager)
export class ResponseProfileManager {
  @inject(Logger) protected logger: Logger;
  @inject(Signalr) protected signalr: Signalr;
  @inject(ResponseProfileStore) protected responseProfileStore: ResponseProfileStore;
  @inject(PriorityManager) protected priorityManager: PriorityManager;

  protected loaded = false;

  get hasLoaded() {
    return this.loaded;
  }

  find(id?: string | number) {
    return this.responseProfileStore.find(id);
  }

  get all() {
    return this.responseProfileStore.all();
  }

  reset() {
    this.loaded = false;
    this.responseProfileStore.set([]);
  }

  /**
   * Convert time string (HH:mm:ss) to seconds
   */
  private timeToSeconds(timeStr: string): number {
    if (!timeStr) return 0;
    const parts = timeStr.split(':');
    if (parts.length !== 3) return 0;

    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    const seconds = parseInt(parts[2], 10);

    return hours * 3600 + minutes * 60 + seconds;
  }

  /**
   * Convert response profile status info to IResponseTimeStamp array
   */
  private mapToResponseTimeStamp(statusInfoArray: IStatusTimeInfo[]): IResponseTimeStamp[] {
    const responseTimeStamps: IResponseTimeStamp[] = [];

    statusInfoArray.forEach((statusInfo: IStatusTimeInfo) => {
      responseTimeStamps.push({
        status: parseInt(String(statusInfo.statusInfo.id), 10),
        seconds: this.timeToSeconds(statusInfo.statusTimer),
        type: parseInt(String(statusInfo.whenStatusChange.id), 10),
        lateCapture: parseInt(String(statusInfo.lateReasonRequired), 10),
      });
    });

    return responseTimeStamps;
  }

  /**
   * Update priority with response profile information
   */
  private updatePriorityResponseProfile(priorityId: string, responseProfile: IResponseProfile) {
    const priority = this.priorityManager.find(priorityId);

    if (!priority) {
      return;
    }

    const emptyResponseTimeStamp: IResponseTimeStamp[] = [];

    // If response profile status is 0 (inactive), clear the response profile
    if (responseProfile.status === 0) {
      priority.responseProfileInfo = {
        id: Number(responseProfile.responseProfileId),
        description: responseProfile.responseProfileName,
        responseTimeStamp: emptyResponseTimeStamp,
      };
    }
    else {
      // Map the response profile to IResponseProfileInfo
      const responseTimeStamp = this.mapToResponseTimeStamp(responseProfile.responseProfileStatusInfo);

      priority.responseProfileInfo = {
        id: Number(responseProfile.responseProfileId),
        description: responseProfile.responseProfileName,
        responseTimeStamp,
      };
    }
  }

  updateResponseProfiles(responseProfiles: IResponseProfile[]) {
    this.loaded = true;

    // Store the response profiles
    this.responseProfileStore.update(...responseProfiles.map((rp) => new ResponseProfile(rp)));

    // Update each priority with its corresponding response profile information
    responseProfiles.forEach((responseProfile: IResponseProfile) => {
      // Get priority IDs from the priorityInfo array
      responseProfile.priorityInfo.forEach((priorityInfo) => {
        const priorityId = priorityInfo.id;
        this.updatePriorityResponseProfile(priorityId, responseProfile);
      });
    });
  }

  @SignalrMessage('ResponseProfileRespMsg')
  protected onResponseProfileRespMsg(response: IResponseProfileRespMsg) {
    this.updateResponseProfiles(response.responseProfiles);
  }
}
